import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Paper,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Grid,
} from "@mui/material";
import {
  ArrowBack,
  VolumeUp,
  Translate,
  TextFields,
} from "@mui/icons-material";

const LessonTest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [testWords, setTestWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState([]);
  const [testMode, setTestMode] = useState(null); // "meaning" hoặc "word"

  useEffect(() => {
    const savedLessons = JSON.parse(localStorage.getItem("lessons") || "[]");
    const currentLesson = savedLessons.find((l) => l.id === id);
    if (currentLesson) {
      setLesson(currentLesson);
      // Xáo trộn từ vựng
      const shuffled = [...currentLesson.words].sort(() => Math.random() - 0.5);
      setTestWords(shuffled);
    } else {
      navigate("/");
    }
  }, [id, navigate]);

  const handlePlayAudio = async (word) => {
    try {
      const response = await fetch(
        `https://api.dictionaryapi.dev/api/v2/entries/en/${word}`
      );
      const data = await response.json();
      if (data[0]?.phonetics?.[0]?.audio) {
        const audio = new Audio(data[0].phonetics[0].audio);
        audio.play();
      }
    } catch (error) {
      console.error("Error playing audio:", error);
    }
  };

  const handleCheckAnswer = () => {
    const currentWord = testWords[currentIndex];
    const isCorrect =
      userAnswer.toLowerCase().trim() ===
      (testMode === "meaning" ? currentWord.meaning : currentWord.word)
        .toLowerCase()
        .trim();

    const newResults = [
      ...results,
      {
        ...currentWord,
        userAnswer: userAnswer,
        isCorrect: isCorrect,
      },
    ];
    setResults(newResults);

    if (currentIndex < testWords.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setUserAnswer("");
    } else {
      setShowResults(true);
    }
  };

  const handleRestart = () => {
    const shuffled = [...lesson.words].sort(() => Math.random() - 0.5);
    setTestWords(shuffled);
    setCurrentIndex(0);
    setUserAnswer("");
    setShowResults(false);
    setResults([]);
    setTestMode(null);
  };

  if (!lesson) return null;

  if (!testMode) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
            <IconButton
              onClick={() => navigate(`/lesson/${lesson.id}`)}
              sx={{ mr: 2 }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
              Chọn chế độ kiểm tra: {lesson.name}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: 6,
                    transform: "translateY(-4px)",
                    transition: "all 0.3s",
                  },
                }}
                onClick={() => setTestMode("meaning")}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Translate
                      sx={{ fontSize: 40, mr: 2, color: "primary.main" }}
                    />
                    <Typography variant="h5">Nhập nghĩa</Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary">
                    Hiển thị từ vựng tiếng Anh, bạn cần nhập nghĩa tiếng Việt
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: 6,
                    transform: "translateY(-4px)",
                    transition: "all 0.3s",
                  },
                }}
                onClick={() => setTestMode("word")}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <TextFields
                      sx={{ fontSize: 40, mr: 2, color: "primary.main" }}
                    />
                    <Typography variant="h5">Nhập từ</Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary">
                    Hiển thị nghĩa tiếng Việt, bạn cần nhập từ vựng tiếng Anh
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <IconButton
            onClick={() => navigate(`/lesson/${lesson.id}`)}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
            Kiểm tra: {lesson.name}
          </Typography>
        </Box>

        {!showResults ? (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Câu {currentIndex + 1}/{testWords.length}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>
                  {testMode === "meaning"
                    ? testWords[currentIndex]?.word
                    : testWords[currentIndex]?.meaning}
                </Typography>
                {testMode === "meaning" && (
                  <IconButton
                    onClick={() =>
                      handlePlayAudio(testWords[currentIndex]?.word)
                    }
                  >
                    <VolumeUp />
                  </IconButton>
                )}
              </Box>
              {testMode === "meaning" && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {testWords[currentIndex]?.pronunciation} -{" "}
                  {testWords[currentIndex]?.type}
                </Typography>
              )}
            </Box>
            <TextField
              fullWidth
              label={testMode === "meaning" ? "Nhập nghĩa" : "Nhập từ"}
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleCheckAnswer();
                }
              }}
              autoFocus
            />
            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={handleCheckAnswer}
                disabled={!userAnswer.trim()}
              >
                Kiểm tra
              </Button>
            </Box>
          </Paper>
        ) : (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Kết quả kiểm tra
              </Typography>
              <Typography variant="body1" gutterBottom>
                Số câu đúng: {results.filter((r) => r.isCorrect).length}/
                {results.length}
              </Typography>
            </Box>
            <List>
              {results.map((result, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                            {testMode === "meaning"
                              ? result.word
                              : result.meaning}
                          </Typography>
                          <Typography
                            variant="body2"
                            color={
                              result.isCorrect ? "success.main" : "error.main"
                            }
                          >
                            {result.isCorrect ? "Đúng" : "Sai"}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.primary"
                          >
                            Đáp án:{" "}
                            {testMode === "meaning"
                              ? result.meaning
                              : result.word}
                          </Typography>
                          <br />
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                          >
                            Bạn trả lời: {result.userAnswer}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {index < results.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={handleRestart}
                sx={{ mr: 2 }}
              >
                Làm lại
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(`/lesson/${lesson.id}`)}
              >
                Quay lại
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default LessonTest;
